import React, { Fragment, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Button, Card, Row, Col, Form } from "react-bootstrap";
import { db } from "../../../../firebase";
import {
  collection,
  collectionGroup,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  updateDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { useAuth } from "../../../../context/AuthContext";
import PageTitle from "../../../layouts/PageTitle";
import PatientSelector from "./PatientSelector";
import OrderProductSelector from "./OrderProductSelector";
import OrderSummary from "./OrderSummary";
import swal from "sweetalert";
import { createSaleOrderWithLots } from "../../../../utils/accessoryLotInventory";
import { normalizeCpapDeviceNo } from "./cpapDeviceNumber";
import {
  calculateWarrantyEndDate,
  DEFAULT_WARRANTY_PERIOD,
  formatWarrantyDate,
} from "../warrantyUtils";

const getLatestSleepStudyOrder = async (hn) => {
  if (!hn) return "";

  const bookingsQuery = query(
    collectionGroup(db, "bookings"),
    where("extendedProps.hn", "==", hn),
    orderBy("extendedProps.latestSleepStudyDate", "desc"),
    limit(1),
  );
  const bookingsSnapshot = await getDocs(bookingsQuery);

  if (bookingsSnapshot.empty) return "";

  const bookingData = bookingsSnapshot.docs[0].data();
  return String(
    (bookingData.extendedProps && bookingData.extendedProps.order) || "",
  ).trim();
};

const saleOrderReceiptExists = async (receiptNo) => {
  if (!receiptNo) return false;

  const duplicateQuery = query(
    collection(db, "saleOrders"),
    where("receiptNo", "==", receiptNo),
    limit(1),
  );
  const duplicateSnapshot = await getDocs(duplicateQuery);

  return !duplicateSnapshot.empty;
};

async function getCurrentSequence() {
  const sequenceRef = doc(db, "sequenceTracker", "saleOrderSequence");

  try {
    const sequenceDoc = await getDoc(sequenceRef);

    if (!sequenceDoc.exists()) {
      // Initialize sequence document if it doesn't exist
      const currentDate = new Date();
      const thaiYear = currentDate.getFullYear() + 543;
      const year = thaiYear;
      const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");

      await setDoc(sequenceRef, {
        current: 0,
        month: month,
        year: year,
      });
      return "001";
    }

    const data = sequenceDoc.data();
    const currentDate = new Date();
    const thaiYear = currentDate.getFullYear() + 543;
    const currentYear = thaiYear;
    const currentMonth = (currentDate.getMonth() + 1)
      .toString()
      .padStart(2, "0");

    // Check if we need to reset for new month/year
    let previewSequence;
    if (data.year !== currentYear || data.month !== currentMonth) {
      previewSequence = 1;
    } else {
      // Show the current sequence number (don't add 1 for preview)
      previewSequence = data.current;
    }

    return previewSequence.toString().padStart(3, "0");
  } catch (e) {
    console.error("Failed to get current sequence: ", e);
    throw e;
  }
}

async function getNextSequence() {
  const sequenceRef = doc(db, "sequenceTracker", "saleOrderSequence");

  try {
    const sequenceDoc = await getDoc(sequenceRef);

    if (!sequenceDoc.exists()) {
      // Initialize sequence document if it doesn't exist
      const currentDate = new Date();
      const thaiYear = currentDate.getFullYear() + 543;
      const year = thaiYear;
      const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");

      await setDoc(sequenceRef, {
        current: 1,
        month: month,
        year: year,
      });
      return "001";
    }

    const data = sequenceDoc.data();
    const currentDate = new Date();
    const thaiYear = currentDate.getFullYear() + 543;
    const currentYear = thaiYear;
    const currentMonth = (currentDate.getMonth() + 1)
      .toString()
      .padStart(2, "0");

    // Use current number and increment for next time
    let currentSequence;
    if (data.year !== currentYear || data.month !== currentMonth) {
      currentSequence = 1;
    } else {
      currentSequence = data.current;
    }

    // Update database to next number for future use
    let updateData = {
      current: currentSequence + 1,
      month: currentMonth,
      year: currentYear,
    };

    await updateDoc(sequenceRef, updateData);

    return currentSequence.toString().padStart(3, "0");
  } catch (e) {
    console.error("Failed to get sequence: ", e);
    throw e;
  }
}

const generatePreviewOrderNumber = async () => {
  try {
    const currentDate = new Date();
    const thaiYear = currentDate.getFullYear() + 543;
    const year = thaiYear.toString().slice(2);
    const month = (currentDate.getMonth() + 1).toString().padStart(2, "0");

    const sequence = await getCurrentSequence();
    return `IGC${year}${month}${sequence}`;
  } catch (error) {
    console.error("Error generating preview order number:", error);
    // Fallback to timestamp-based number
    const now = new Date();
    const year = now.getFullYear().toString().substr(-2);
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    const time = now.getTime().toString().slice(-6);
    return `ORD${year}${month}${day}${time}`;
  }
};

const NewOrderForm = () => {
  const { currentUser } = useAuth();
  const history = useHistory();

  const [step, setStep] = useState(1); // 1: Patient, 2: Products, 3: Summary
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState({
    cpaps: [],
    accessories: [],
  });
  const [orderDetails, setOrderDetails] = useState(() => {
    const receiptDate = formatWarrantyDate(new Date());
    const warrantyEndDate = formatWarrantyDate(
      calculateWarrantyEndDate(DEFAULT_WARRANTY_PERIOD, receiptDate),
    );

    return {
      receiptNo: "Loading...",
      receiptDate,
      status: "เตรียมเครื่อง",
      sales: "",
      note: "",
      amount: "",
      salePackage: "",
      activity: "purchase",
      warrantyPeriod: DEFAULT_WARRANTY_PERIOD,
      warrantyStartDate: receiptDate,
      warrantyEndDate,
    };
  });
  const [salesOptions, setSalesOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleOrderDetailsChange = (field, value) => {
    const updated = { ...orderDetails, [field]: value };

    // Auto-calculate warranty dates when the period or start date changes.
    if (field === "warrantyPeriod") {
      const warrantyStartDate =
        orderDetails.warrantyStartDate || orderDetails.receiptDate;
      updated.warrantyStartDate = warrantyStartDate;
      updated.warrantyEndDate = formatWarrantyDate(
        calculateWarrantyEndDate(value, warrantyStartDate),
      );
    } else if (field === "receiptDate") {
      updated.warrantyStartDate = value;
      updated.warrantyEndDate = formatWarrantyDate(
        calculateWarrantyEndDate(orderDetails.warrantyPeriod, value),
      );
    } else if (field === "warrantyStartDate") {
      updated.warrantyEndDate = formatWarrantyDate(
        calculateWarrantyEndDate(orderDetails.warrantyPeriod, value),
      );
    }

    setOrderDetails(updated);
  };

  useEffect(() => {
    const initializeOrder = async () => {
      try {
        // Generate preview order number (doesn't update sequence)
        const orderNumber = await generatePreviewOrderNumber();
        setOrderDetails((prev) => ({
          ...prev,
          receiptNo: orderNumber,
        }));

        // Load sales representatives
        const salesQuery = query(
          collection(db, "sales"),
          where("status", "==", "Available")
        );
        const salesSnapshot = await getDocs(salesQuery);
        const salesList = salesSnapshot.docs.map((doc) => ({
          id: doc.id,
          name: doc.data().name,
        }));
        setSalesOptions(salesList);
      } catch (error) {
        console.error("Error initializing order:", error);
        // Fallback order number
        const now = new Date();
        const year = now.getFullYear().toString().substr(-2);
        const month = (now.getMonth() + 1).toString().padStart(2, "0");
        const day = now.getDate().toString().padStart(2, "0");
        const time = now.getTime().toString().slice(-6);
        setOrderDetails((prev) => ({
          ...prev,
          receiptNo: `ORD${year}${month}${day}${time}`,
        }));
      }
    };

    initializeOrder();
  }, []);

  const handleNextStep = () => {
    if (step === 1 && !selectedPatient) {
      swal("Error", "Please select a patient first", "error");
      return;
    }
    if (
      step === 2 &&
      selectedProducts.cpaps.length === 0 &&
      selectedProducts.accessories.length === 0
    ) {
      swal("Error", "Please select at least one product", "error");
      return;
    }
    setStep(step + 1);
  };

  const handlePrevStep = () => {
    setStep(step - 1);
  };

  const handleSubmitOrder = async () => {
    if (!orderDetails.warrantyStartDate || !orderDetails.warrantyEndDate) {
      swal(
        "Warranty dates required",
        "Enter a valid Warranty Start Date to calculate the Warranty End Date.",
        "warning",
      );
      return;
    }

    setLoading(true);

    try {
      // Use the current preview order number for creating orders
      const orderNumber = String(orderDetails.receiptNo || "").trim();
      const duplicateReceipt = await saleOrderReceiptExists(orderNumber);

      if (duplicateReceipt) {
        const createDuplicate = await swal({
          title: "Receipt number already used",
          text: `Sale Order No. ${orderNumber} is already in use. Do you want to create another sale order with the same number?`,
          icon: "warning",
          buttons: ["Cancel", "Create Anyway"],
          dangerMode: true,
        });

        if (!createDuplicate) return;
      }

      // Validate stock for accessories before creating order
      for (const accessory of selectedProducts.accessories) {
        const accessoryDoc = await getDoc(doc(db, "accessories", accessory.id));
        if (!accessoryDoc.exists()) {
          throw new Error(`Accessory ${accessory.itemCode} not found`);
        }

        const currentStockQuantity = accessoryDoc.data().stockQuantity;
        const orderQuantity = accessory.quantity || 1;

        if (currentStockQuantity < orderQuantity) {
          throw new Error(
            `Insufficient stock for ${accessory.itemName}. Available: ${currentStockQuantity}, Requested: ${orderQuantity}`
          );
        }
      }

      // Create items array with all products
      const items = [];

      // Add CPAP items
      selectedProducts.cpaps.forEach((cpap) => {
        const cpapItem = {
          productId: cpap.id,
          productName: cpap.model,
          productSn: cpap.serialNumber,
          price: cpap.amount || cpap.price || 0,
          quantity: 1,
          size: "",
          productBrand: cpap.brand,
          productType: "CPAP",
        };

        const deviceNo = normalizeCpapDeviceNo(cpap.deviceNo);
        if (deviceNo) {
          cpapItem.deviceNo = deviceNo;
        }

        items.push(cpapItem);
      });

      // Add accessory items
      selectedProducts.accessories.forEach((accessory) => {
        items.push({
          productId: accessory.id,
          productName: accessory.itemName,
          productSn: accessory.itemCode,
          price: accessory.unitPrice || 0,
          quantity: accessory.quantity || 1,
          size: accessory.size || "",
          productBrand: accessory.brand,
          productType: accessory.category,
          itemType: "Accessory", // Add this field to make detection easier
        });
      });

      // Use Grand Total from Step 3 (includes package inclusion logic)
      const totalAmount = orderDetails.grandTotal || 0;
      const sleepStudyOrder = await getLatestSleepStudyOrder(
        selectedPatient.hn,
      );

      // Create single order document
      const orderData = {
        receiptNo: orderNumber,
        hn: selectedPatient.hn,
        firstname: selectedPatient.firstnameTh,
        lastname: selectedPatient.lastnameTh,
        items: items,
        totalAmount: totalAmount,
        receiptDate: new Date(orderDetails.receiptDate),
        status: orderDetails.status,
        sales: orderDetails.sales,
        salePackage: orderDetails.salePackage,
        note: orderDetails.note,
        refCustomer: selectedPatient.refCustomer || "",
        refHospital: selectedPatient.refHn || "",
        nodeRef: selectedPatient.nodeRef || "",
        mobile: selectedPatient.mobile || "",
        sleepStudyOrder,
        warrantyPeriod: orderDetails.warrantyPeriod,
        warrantyStartDate: new Date(orderDetails.warrantyStartDate),
        warrantyEndDate: new Date(orderDetails.warrantyEndDate),
        accessoryPackageStatus: orderDetails.accessoryPackageStatus || {},
        createdAt: new Date(),
        createdBy: currentUser.displayName || currentUser.email,
        updatedAt: new Date(),
        updatedBy: currentUser.displayName || currentUser.email,
      };

      // Create the single order
      const orderDocRef = await createSaleOrderWithLots(db, orderData, currentUser);

      // Create patient order logs and product logs for CPAPs
      const loggingPromises = [];

      // Create single patient order log for the order (following the pattern from orders.js)
      if (selectedProducts.cpaps.length > 0) {
        // Use first CPAP for the main patient order log (following original pattern)
        const mainCpap = selectedProducts.cpaps[0];
        const patientOrderLog = {
          receiptNo: orderNumber,
          productId: mainCpap.id,
          productSn: mainCpap.serialNumber,
          activity: "purchase",
          receiptDate: new Date(orderDetails.receiptDate),
          status: orderDetails.status,
          createdAt: new Date(),
          sales: orderDetails.sales,
          createdBy: currentUser.displayName || currentUser.email,
        };

        // Add to patients/{hn}/orders/{orderId}
        loggingPromises.push(
          setDoc(
            doc(db, "patients", selectedPatient.hn, "orders", orderDocRef.id),
            patientOrderLog
          )
        );
      }

      // Create product log for each CPAP in products/{productId}/logs/{orderId}
      selectedProducts.cpaps.forEach((cpap) => {
        const productLog = {
          hn: selectedPatient.hn,
          firstname: selectedPatient.firstnameTh,
          lastname: selectedPatient.lastnameTh,
          receiptNo: orderNumber,
          productId: cpap.id,
          productSn: cpap.serialNumber,
          activity: "purchase",
          status: orderDetails.status,
          createdAt: new Date(),
          sales: orderDetails.sales,
          createdBy: currentUser.displayName || currentUser.email,
        };

        loggingPromises.push(
          setDoc(
            doc(db, "products", cpap.id, "logs", orderDocRef.id),
            productLog
          )
        );
      });

      // Execute logging operations
      await Promise.all(loggingPromises);

      // Update product statuses and stock in parallel
      const updatePromises = [];

      // Update CPAP status to sold
      selectedProducts.cpaps.forEach((cpap) => {
        updatePromises.push(
          updateDoc(doc(db, "products", cpap.id), {
            status: "ขาย",
            sales: orderDetails.sales || "",
            soldDate: new Date(),
            soldTo: selectedPatient.hn,
            updatedAt: new Date(),
            updatedBy: currentUser.displayName || currentUser.email,
          })
        );
      });

      await Promise.all(updatePromises);

      // Only update sequence number after successful order creation
      await getNextSequence();

      swal("Success", "Order created successfully!", "success").then(() => {
        history.push("/sale-order-list");
      });
    } catch (error) {
      console.error("Error creating order:", error);
      swal("Error", `Failed to create order: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between">
        <div
          className={`text-center ${step >= 1 ? "text-primary" : "text-muted"}`}
        >
          <div
            className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
              step >= 1 ? "bg-primary text-white" : "bg-light text-muted"
            }`}
            style={{ width: "40px", height: "40px" }}
          >
            1
          </div>
          <div className="mt-1">Patient</div>
        </div>
        <div
          className={`text-center ${step >= 2 ? "text-primary" : "text-muted"}`}
        >
          <div
            className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
              step >= 2 ? "bg-primary text-white" : "bg-light text-muted"
            }`}
            style={{ width: "40px", height: "40px" }}
          >
            2
          </div>
          <div className="mt-1">Products</div>
        </div>
        <div
          className={`text-center ${step >= 3 ? "text-primary" : "text-muted"}`}
        >
          <div
            className={`rounded-circle d-inline-flex align-items-center justify-content-center ${
              step >= 3 ? "bg-primary text-white" : "bg-light text-muted"
            }`}
            style={{ width: "40px", height: "40px" }}
          >
            3
          </div>
          <div className="mt-1">Summary</div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <PatientSelector
            selectedPatient={selectedPatient}
            onSelectPatient={setSelectedPatient}
          />
        );
      case 2:
        return (
          <OrderProductSelector
            selectedProducts={selectedProducts}
            onUpdateProducts={setSelectedProducts}
            orderDetails={orderDetails}
            onUpdateOrderDetails={handleOrderDetailsChange}
          />
        );
      case 3:
        return (
          <OrderSummary
            patient={selectedPatient}
            products={selectedProducts}
            orderDetails={orderDetails}
            onUpdateOrderDetails={handleOrderDetailsChange}
            salesOptions={salesOptions}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Fragment>
      <PageTitle activeMenu="New Order" motherMenu="Sales" />

      <Card>
        <Card.Header>
          <Card.Title>Create New Order</Card.Title>
        </Card.Header>
        <Card.Body
          style={{
            // maxHeight: "calc(100vh - 300px)",
            overflowY: "auto",
          }}
        >
          {renderStepIndicator()}

          {renderStepContent()}
        </Card.Body>
        <Card.Footer className="bg-white border-top">
          <div className="d-flex justify-content-between">
            <div>
              {step > 1 && (
                <Button variant="outline-secondary" onClick={handlePrevStep}>
                  Previous
                </Button>
              )}
            </div>
            <div>
              {step < 3 ? (
                <Button variant="primary" onClick={handleNextStep}>
                  Next
                </Button>
              ) : (
                <Button
                  variant="success"
                  onClick={handleSubmitOrder}
                  disabled={loading}
                >
                  {loading ? "Creating Order..." : "Create Order"}
                </Button>
              )}
            </div>
          </div>
        </Card.Footer>
      </Card>
    </Fragment>
  );
};

export default NewOrderForm;
